"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Search, FileText, MapPin, Star, Code as Code2, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2, Sparkles, UserCheck } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const auditSteps = [{
  stepNumber: "01",
  title: "Technical & On-Page SEO Audit",
  subtitle: "Deep Crawl & Infrastructure",
  description: "I perform a comprehensive technical crawl to uncover indexing blockers, page speed bottlenecks, site architecture flaws, and on-page optimization gaps.",
  iconName: "Search",
  deliverables: "Full Technical Audit, Prioritized Action Plan, Code Fix Guide"
}, {
  stepNumber: "02",
  title: "Content & Keyword Optimization",
  subtitle: "Search Intent Alignment",
  description: "Refining existing pages and mapping target keyword entities. I optimize titles, headings, and semantic structure so search engines recognize your core authority.",
  iconName: "FileText",
  deliverables: "Keyword Mapping Matrix, Content Gap Plan, On-Page Rewriting"
}, {
  stepNumber: "03",
  title: "Local Citations & NAP Consistency",
  subtitle: "Directory & Entity Authority",
  description: "Auditing and building consistent Name, Address, and Phone (NAP) citations across key UK and international platforms to reinforce geographic relevance.",
  iconName: "MapPin",
  deliverables: "Citation Audit, Duplicate Cleanup, High-Authority Submissions"
}, {
  stepNumber: "04",
  title: "Reputation & Review Strategy",
  subtitle: "Trust Signal Amplification",
  description: "Implementing structured workflows for capturing authentic customer reviews and optimizing business profile engagement to boost conversion trust.",
  iconName: "Star",
  deliverables: "Review Acquisition Blueprint, Business Profile Strategy"
}, {
  stepNumber: "05",
  title: "Schema & Structured Data",
  subtitle: "Rich Snippets & AI Search",
  description: "Deploying custom JSON-LD schema markup so traditional search engines and emerging AI search platforms precisely parse your services and credentials.",
  iconName: "Code2",
  deliverables: "Custom Schema Markup, AI Search Clarity Check, Rich Snippets"
}, {
  stepNumber: "06",
  title: "Transparent Reporting & Strategy",
  subtitle: "Direct Consultant Accountability",
  description: "No automated data dumps. You receive concise monthly reporting focused on real search visibility growth, keyword movements, and qualified organic leads.",
  iconName: "BarChart3",
  deliverables: "Monthly Performance Brief, Conversion Analysis, Updated Roadmap"
}];
export default function AuditProcess() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".step-card", {
        y: 30
      });
      gsap.to(".step-card", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.12,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
      gsap.set(".connector-line", {
        scaleX: 0,
        transformOrigin: "left center"
      });
      gsap.to(".connector-line", {
        scaleX: 1,
        duration: 0.8,
        ease: "power2.inOut",
        scrollTrigger: {
          trigger: el,
          start: "top 75%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".step-card", {
        opacity: 1,
        y: 0
      });
      gsap.set(".connector-line", {
        scaleX: 1
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  const getStepIcon = (name: string) => {
    switch (name) {
      case "Search":
        return <Search className="size-5 text-primary" />;
      case "FileText":
        return <FileText className="size-5 text-primary" />;
      case "MapPin":
        return <MapPin className="size-5 text-primary" />;
      case "Star":
        return <Star className="size-5 text-primary" />;
      case "Code2":
        return <Code2 className="size-5 text-primary" />;
      case "BarChart3":
        return <BarChart3 className="size-5 text-primary" />;
      default:
        return <CheckCircle2 className="size-5 text-primary" />;
    }
  };
  return <section id="stepbystepauditprocess" data-nav="light" className="bg-background py-20 lg:py-28 relative overflow-hidden">
      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 bg-secondary text-secondary-foreground px-3 py-1 rounded-full border border-accent/20">
            <Sparkles className="size-3.5 mr-1 text-accent" />
            Hands-On SEO Methodology
          </Badge>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight mb-6">
            Step-by-Step SEO Audit & Optimization Process
          </h2>
          
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            A methodical, zero-fluff framework tailored for UK and international growth. You work directly with me — Yassine Essaadi — through every diagnostic audit, structural improvement, and strategic milestone.
          </p>

          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted text-xs font-medium text-foreground">
            <UserCheck className="size-4 text-primary" />
            <span>Direct Consultant Execution &bull; No Account Managers &bull; Pure SEO Expertise</span>
          </div>
        </div>

        {/* Audit Steps Grid with visual connector */}
        <div className="relative">
          {/* Subtle horizontal guideline for desktop */}
          <div className="connector-line hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-accent/30 -translate-y-1/2 pointer-events-none z-0" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 relative z-10">
            {auditSteps.map((step, i) => <div key={step.stepNumber} data-index={i} className="step-card h-full" style={{
            opacity: 0
          }}>
                <Card className="h-full bg-card hover:shadow-lg transition-shadow duration-300 border-border/80 rounded-xl relative flex flex-col justify-between">
                  <CardHeader className="space-y-3 pb-4">
                    <div className="flex items-center justify-between">
                      {/* Step Number Badge */}
                      <span className="bg-primary text-primary-foreground font-mono font-bold text-sm px-3 py-1 rounded-md shadow-xs">
                        Step {step.stepNumber}
                      </span>
                      
                      {/* Step Icon */}
                      <div className="p-2.5 rounded-lg bg-accent/15 border border-accent/30 flex items-center justify-center">
                        {getStepIcon(step.iconName)}
                      </div>
                    </div>

                    <div>
                      <span className="text-xs font-semibold text-accent uppercase tracking-wider block mb-1">
                        {step.subtitle}
                      </span>
                      <CardTitle className="text-xl font-bold font-serif text-card-foreground">
                        {step.title}
                      </CardTitle>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-0 flex-1 flex flex-col justify-between">
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                      {step.description}
                    </CardDescription>

                    <div className="pt-3 border-t border-border/60">
                      <div className="bg-muted/60 rounded-lg p-3 flex items-start gap-2.5">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <div>
                          <span className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wide block">
                            Key Deliverable
                          </span>
                          <span className="text-xs font-medium text-foreground">
                            {step.deliverables}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>

        {/* Process CTA Strip */}
        <div className="mt-16 lg:mt-20 rounded-2xl bg-muted/50 border border-border p-8 lg:p-10 max-w-4xl mx-auto shadow-xs">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="space-y-2 text-center md:text-left">
              <h3 className="text-2xl font-bold font-serif text-foreground">
                Ready for a Comprehensive SEO Audit?
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base max-w-xl">
                Let&apos;s review your website&apos;s technical health, keyword opportunities, and search visibility together in a free initial consultation.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 shrink-0 w-full md:w-auto">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent hover:text-accent-foreground font-medium">
                <Link to="/CaseStudiesPage">
                  Explore Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}